import type { Editor } from '@tiptap/react'
import { Button } from '../../ui/Button'

interface ToolbarProps {
  editor: Editor
}

const BoldIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/></svg>
const ItalicIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></svg>
const CodeIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><polyline points="16,18 22,12 16,6"/><polyline points="8,6 2,12 8,18"/></svg>
const LinkIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
const ListIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
const OrderedIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></svg>
const QuoteIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor"><path d="M10 8c-1.1 0-2 .9-2 2v4h4v-4H8l2-4H8l-2 4zm8 0c-1.1 0-2 .9-2 2v4h4v-4h-4l2-4h-2l-2 4z" opacity={0.7}/></svg>
const HighlightIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
const TableIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
const ImageIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>
const DividerIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="2" y1="12" x2="22" y2="12"/></svg>
const ChecklistIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>

function Sep() {
  return <div style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px', flexShrink: 0 }} />
}

export function EditorToolbar({ editor }: ToolbarProps) {
  function setLink() {
    const url = prompt('Link URL:')
    if (url) editor.chain().focus().setLink({ href: url }).run()
  }

  function addImage() {
    const url = prompt('Image URL:')
    if (url) editor.chain().focus().setImage({ src: url }).run()
  }

  return (
    <div style={{
      display: 'flex', gap: 1, padding: '6px 8px', flexWrap: 'wrap',
      background: 'var(--bg-3)', borderRadius: 8,
      border: '1px solid var(--border-h)', marginBottom: 12,
      alignItems: 'center',
    }}>
      {/* Text formatting */}
      <Button variant="ghost" iconOnly size="sm" title="Bold (Cmd+B)"
        icon={BoldIcon} onClick={() => editor.chain().focus().toggleBold().run()}
        style={editor.isActive('bold') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      />
      <Button variant="ghost" iconOnly size="sm" title="Italic (Cmd+I)"
        icon={ItalicIcon} onClick={() => editor.chain().focus().toggleItalic().run()}
        style={editor.isActive('italic') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      />
      <Button variant="ghost" iconOnly size="sm" title="Highlight (Cmd+Shift+H)"
        icon={HighlightIcon} onClick={() => editor.chain().focus().toggleHighlight().run()}
        style={editor.isActive('highlight') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      />
      <Button variant="ghost" iconOnly size="sm" title="Inline Code (Cmd+E)"
        icon={CodeIcon} onClick={() => editor.chain().focus().toggleCode().run()}
        style={editor.isActive('code') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      />

      <Sep />

      {/* Headings */}
      <Button variant="ghost" size="sm" title="Heading 1"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        style={editor.isActive('heading', { level: 1 }) ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      >H1</Button>
      <Button variant="ghost" size="sm" title="Heading 2"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        style={editor.isActive('heading', { level: 2 }) ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      >H2</Button>
      <Button variant="ghost" size="sm" title="Heading 3"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        style={editor.isActive('heading', { level: 3 }) ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      >H3</Button>

      <Sep />

      {/* Lists */}
      <Button variant="ghost" iconOnly size="sm" title="Bullet List"
        icon={ListIcon} onClick={() => editor.chain().focus().toggleBulletList().run()}
        style={editor.isActive('bulletList') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      />
      <Button variant="ghost" iconOnly size="sm" title="Numbered List"
        icon={OrderedIcon} onClick={() => editor.chain().focus().toggleOrderedList().run()}
        style={editor.isActive('orderedList') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      />
      <Button variant="ghost" iconOnly size="sm" title="Task List"
        icon={ChecklistIcon} onClick={() => editor.chain().focus().toggleTaskList().run()}
        style={editor.isActive('taskList') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      />

      <Sep />

      {/* Blocks */}
      <Button variant="ghost" iconOnly size="sm" title="Blockquote"
        icon={QuoteIcon} onClick={() => editor.chain().focus().toggleBlockquote().run()}
        style={editor.isActive('blockquote') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      />
      <Button variant="ghost" iconOnly size="sm" title="Code Block"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        style={editor.isActive('codeBlock') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      >{`{}`}</Button>
      <Button variant="ghost" iconOnly size="sm" title="Table"
        icon={TableIcon} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3 }).run()}
      />
      <Button variant="ghost" iconOnly size="sm" title="Image"
        icon={ImageIcon} onClick={addImage}
      />
      <Button variant="ghost" iconOnly size="sm" title="Divider"
        icon={DividerIcon} onClick={() => editor.chain().focus().setHorizontalRule().run()}
      />

      <Sep />

      {/* Link */}
      <Button variant="ghost" iconOnly size="sm" title="Insert Link (Cmd+K)"
        icon={LinkIcon} onClick={setLink}
        style={editor.isActive('link') ? { background: 'var(--accent-m)', color: 'var(--accent)' } : undefined}
      />
    </div>
  )
}
